import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Icon, type IconName } from './Icon';
import '../styles/richtext.css';

/**
 * Editor de texto enriquecido para propuestas y contratos.
 *
 * Pensado para redactar en Word y pegar el documento entero: conserva tablas
 * con bordes, colores de texto y de fondo, imagenes, tamanos y tipos de letra,
 * negritas, listas y titulos. TipTap interpreta el HTML de Office y lo
 * normaliza a un marcado limpio y editable, manteniendo el aspecto.
 */

interface Props {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
}

function ToolButton({ onClick, active, title, icon }: { onClick: () => void; active?: boolean; title: string; icon: IconName }) {
  return (
    <button
      type="button"
      className={`rt-tool${active ? ' is-on' : ''}`}
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    >
      <Icon name={icon} size={15} />
    </button>
  );
}

export function RichText({ value, onChange, minHeight = 360 }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: { openOnClick: false } }),
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: value || '',
    editorProps: {
      attributes: { class: 'rt-content', style: `min-height:${minHeight}px` },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current && !editor.isFocused) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return <div className="rt-loading">Cargando editor...</div>;

  const setColor = () => {
    const c = window.prompt('Color del texto (ej. #b4753a o "red"):', '#1f5f4b');
    if (c) editor.chain().focus().setColor(c).run();
  };
  const setHighlight = () => {
    const c = window.prompt('Color de fondo (ej. #fff3cd):', '#fff3cd');
    if (c) editor.chain().focus().toggleHighlight({ color: c }).run();
  };
  const insertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

  const inTable = editor.isActive('table');

  return (
    <div className="rt">
      <div className="rt-bar">
        <ToolButton icon="bold" title="Negrita" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolButton icon="italic" title="Cursiva" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolButton icon="underline" title="Subrayado" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolButton icon="palette" title="Color del texto" onClick={setColor} />
        <ToolButton icon="highlighter" title="Color de fondo" active={editor.isActive('highlight')} onClick={setHighlight} />
        <span className="rt-sep" />
        <ToolButton icon="heading" title="Titulo" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolButton icon="list" title="Lista con vinetas" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolButton icon="list-ordered" title="Lista numerada" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <ToolButton icon="quote" title="Cita" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <span className="rt-sep" />
        <ToolButton icon="table" title="Insertar tabla" onClick={insertTable} />
        {inTable && (
          <>
            <ToolButton icon="column-plus" title="Anadir columna" onClick={() => editor.chain().focus().addColumnAfter().run()} />
            <ToolButton icon="row-plus" title="Anadir fila" onClick={() => editor.chain().focus().addRowAfter().run()} />
            <ToolButton icon="trash" title="Eliminar tabla" onClick={() => editor.chain().focus().deleteTable().run()} />
          </>
        )}
        <ToolButton icon="minus" title="Separador" onClick={() => editor.chain().focus().setHorizontalRule().run()} />
        <div style={{ flex: 1 }} />
        <ToolButton icon="rotate-ccw" title="Deshacer" onClick={() => editor.chain().focus().undo().run()} />
        <ToolButton icon="rotate-cw" title="Rehacer" onClick={() => editor.chain().focus().redo().run()} />
      </div>
      <EditorContent editor={editor} />
      <div className="rt-hint">Redacta en Word y pega aqui: se conservan tablas, colores, imagenes y tipografia.</div>
    </div>
  );
}
